import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Paper,
  Stack,
  useTheme,
  IconButton,
} from "@mui/material";
import {
  Add,
  Percent,
  Person,
  QrCode,
  Edit,
  Delete,
  Save,
  Cancel,
} from "@mui/icons-material";
import { IRaffle } from "../../../store/interfaces/IRaffle";

interface Seller {
  name: string;
  code: string;
  discount: number;
}

interface SellersProps {
  raffle: IRaffle;
}

export const Sellers = ({ raffle }: SellersProps) => {
  const theme = useTheme();
  const showPremiumAlert = !raffle.isPremium;

  const [sellers, setSellers] = useState<Seller[]>([]);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(0);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editCode, setEditCode] = useState("");
  const [editDiscount, setEditDiscount] = useState(0);

  const handleAddSeller = () => {
    if (!name || !code || discount < 0) return;
    setSellers([...sellers, { name, code, discount }]);
    setName("");
    setCode("");
    setDiscount(0);
  };

  const handleDeleteSeller = (index: number) => {
    const updated = [...sellers];
    updated.splice(index, 1);
    setSellers(updated);
  };

  const handleStartEdit = (index: number) => {
    setEditingIndex(index);
    setEditCode(sellers[index].code);
    setEditDiscount(sellers[index].discount);
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    const updated = [...sellers];
    updated[editingIndex] = {
      ...updated[editingIndex],
      code: editCode,
      discount: editDiscount,
    };
    setSellers(updated);
    setEditingIndex(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  return (
    <Card
      sx={{
        mx: "auto",
        p: 3,
        borderRadius: 3,
        boxShadow: 4,
        maxWidth: 900,
        textAlign: "center",
      }}
    >
      <CardContent
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          overflow: "hidden",
        }}
      >
        {showPremiumAlert && (
          <>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backdropFilter: "blur(2px)",
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 2,
                backgroundColor: "black",
                border: `2px solid white`,
                borderRadius: 2,
                padding: 4,
                boxShadow: 6,
                textAlign: "center",
                maxWidth: 420,
              }}
            >
              <Typography variant="h6" color="warning.main" gutterBottom>
                🚫 Funcionalidad Premium
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Esta funcionalidad permite crear <strong>vendedores con código promocional y descuento</strong> aplicable en la compra.
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={2}>
                Ideal para campañas de marketing o seguimiento de ventas. Disponible solo para rifas con plan Premium.
              </Typography>
            </Box>
          </>
        )}

        <Typography variant="h5" color="primary" gutterBottom>
          🏃🏽‍♂️ Mantenedor de Vendedores
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Agrega vendedores con código único y descuento. Las ventas se reflejarán en el módulo de reportes.
        </Typography>

        <Paper sx={{ p: 3, width: "100%", maxWidth: 700 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Nombre"
                placeholder="Ej. Juan Pérez"
                value={name}
                onChange={(e) => setName(e.target.value)}
                InputProps={{ startAdornment: <Person sx={{ mr: 1 }} /> }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Código promocional"
                placeholder="Ej. JUAN10"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                InputProps={{ startAdornment: <QrCode sx={{ mr: 1 }} /> }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Descuento (%)"
                type="number"
                inputProps={{ min: 0, max: 100 }}
                value={discount}
                onChange={(e) => setDiscount(Number(e.target.value))}
                InputProps={{ startAdornment: <Percent sx={{ mr: 1 }} /> }}
              />
            </Grid>
          </Grid>

          <Button
            onClick={handleAddSeller}
            variant="contained"
            color="primary"
            startIcon={<Add />}
            sx={{ mt: 3 }}
            disabled={!name || !code || discount < 0}
          >
            Agregar vendedor
          </Button>
        </Paper>

        {sellers.length > 0 && (
          <Box sx={{ mt: 4, width: "100%", maxWidth: 700 }}>
            <Typography variant="subtitle1" gutterBottom>
              Vendedores registrados:
            </Typography>
            <Stack spacing={1}>
              {sellers.map((s, index) => (
                <Paper key={index} sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <Box>
                      <Typography fontWeight="bold">{s.name}</Typography>

                      {editingIndex === index ? (
                        <>
                          <TextField
                            label="Código"
                            size="small"
                            sx={{ mt: 1, mr: 2 }}
                            value={editCode}
                            onChange={(e) => setEditCode(e.target.value)}
                          />
                          <TextField
                            label="Descuento (%)"
                            type="number"
                            size="small"
                            sx={{ mt: 1 }}
                            value={editDiscount}
                            onChange={(e) => setEditDiscount(Number(e.target.value))}
                          />
                        </>
                      ) : (
                        <>
                          <Typography variant="body2">Código: {s.code}</Typography>
                          <Typography variant="body2">Descuento: {s.discount}%</Typography>
                        </>
                      )}
                    </Box>

                    <Box sx={{ mt: 1 }}>
                      {editingIndex === index ? (
                        <>
                          <IconButton onClick={handleSaveEdit} color="success">
                            <Save />
                          </IconButton>
                          <IconButton onClick={handleCancelEdit} color="inherit">
                            <Cancel />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton onClick={() => handleStartEdit(index)} color="primary">
                            <Edit />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteSeller(index)} color="error">
                            <Delete />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
